import { ActionContext } from 'vuex';

import * as api from '@/api';
import { createDocumentPath } from '@/utils/hrUtils';
import {
  Contacts,
  Contract,
  ContractDimensionsDTO,
  Document,
  HealthCheck,
  OfficialInformation,
  UserWorkProfile,
} from '@/views/modules/hr/api/types';

// APIs
import HRApi from '@/views/modules/hr/api/client';

const initialState = {
  hrUser: {} as {
    contacts?: Contacts;
    officialInformation?: {
      documents: Document[];
    };
  },
  hrContract: {} as UserWorkProfile,
  hrHealthCheck: {} as HealthCheck,
  hrSelectedUserId: null as string,
  hrSelectedContractId: null as string,
  hrSelectedHealthCheckId: null as string,
  hrDocuments: [] as Document[],
};

export type HRModuleState = typeof initialState;

const hrWorkListClient = new api.HrWorkListClient();
const hrHealthListClient = new api.HrHealthListClient();
const hrApi = new HRApi();

const module = {
  state: initialState,
  getters: {
    hrUser: (state: HRModuleState) => state.hrUser,
    hrContract: (state: HRModuleState) => state.hrContract,
    hrHealthCheck: (state: HRModuleState) => state.hrHealthCheck,
    hrSelectedUserId: (state: HRModuleState) => state.hrSelectedUserId,
    hrSelectedContractId: (state: HRModuleState) => state.hrSelectedContractId,
    hrSelectedHealthCheckId: (state: HRModuleState) => state.hrSelectedHealthCheckId,
    hrAttachments: (state: HRModuleState) => (state.hrDocuments ?? []).map((d) => ({
      ...d,
      path: createDocumentPath(d.folder, d.version, d.fileName),
      disabled: true,
    })),
  },
  mutations: {
    SET_HR_USER(state: HRModuleState, user: any): void {
      state.hrUser = user;
      state.hrDocuments = user?.officialInformation?.documents;
    },
    SET_HR_CONTRACT(state: HRModuleState, contract: UserWorkProfile): void {
      state.hrContract = contract;
      state.hrDocuments = contract?.contract?.documents;
    },
    SET_HR_CONTRACT_DIMENSIONS(state: HRModuleState, dimensions: ContractDimensionsDTO): void {
      state.hrContract.dimensions = dimensions;
    },
    SET_HR_HEALTH_CHECK(state: HRModuleState, healthCheck: HealthCheck): void {
      state.hrHealthCheck = healthCheck;
      state.hrDocuments = healthCheck?.documents;
    },
    SET_HR_SELECTED_USER_ID(state: HRModuleState, userId: string): void {
      state.hrSelectedUserId = userId;
    },
    SET_HR_SELECTED_CONTRACT_ID(state: any, id: string): void {
      state.hrSelectedContractId = id;
    },
    SET_HR_SELECTED_HEALTH_CHECK_ID(state: any, id: string): void {
      state.hrSelectedHealthCheckId = id;
    },
  },
  actions: {
    setHRUser({ commit }, user: any): void {
      commit('SET_HR_USER', user);
    },
    setHRContract({ commit }, contract: any): void {
      commit('SET_HR_CONTRACT', contract);
    },
    setHRContractDimensions({ commit }, dimensions: ContractDimensionsDTO): void {
      commit('SET_HR_CONTRACT_DIMENSIONS', dimensions);
    },
    setHRHealthCheck({ commit }, healthCheck: HealthCheck): void {
      commit('SET_HR_HEALTH_CHECK', healthCheck);
    },
    setHRSelectedUserId({ commit }, userId: string): void {
      commit('SET_HR_SELECTED_USER_ID', userId);
      if (userId == null) {
        commit('SET_HR_SELECTED_CONTRACT_ID', null);
        commit('SET_HR_SELECTED_HEALTH_CHECK_ID', null);
      }
    },
    selectContract({ commit }, item: api.UserContractListItem) {
      commit('SET_HR_SELECTED_USER_ID', item.userId);
      commit('SET_HR_SELECTED_CONTRACT_ID', item.contractId);
    },
    selectHealthCheck({ commit }, item: api.UserHealthListItem) {
      commit('SET_HR_SELECTED_USER_ID', item.employee);
      commit('SET_HR_SELECTED_HEALTH_CHECK_ID', item.id);
    },
    async loadRelatedUser({ state, dispatch }) {
      if (!state.hrSelectedUserId) {
        return;
      }
      const row = { userId: state.hrSelectedUserId };
      if (row) {
        dispatch('setSelectedRecord', row);
        dispatch('rightPanelOpen');
      }
    },
    async loadRelatedContract({ state, dispatch, commit }) {
      if (!state.hrSelectedUserId) {
        return;
      }
      const result = await hrWorkListClient.searchContracts({
        filterCriteria: `user_contracts_employee:eq:${state.hrSelectedUserId}` as any,
        sortCriteria: 'user_contracts_id:desc' as any,
        limit: 1,
      });
      const row = result.results[0];
      if (row) {
        commit('SET_HR_SELECTED_CONTRACT_ID', row.contractId);
        dispatch('setSelectedRecord', row);
        dispatch('rightPanelOpen');
      }
    },
    async loadRelatedHealthCheck({ state, dispatch, commit }: ActionContext<HRModuleState, {}>) {
      if (!state.hrSelectedUserId) {
        return;
      }
      const result = await hrHealthListClient.searchUserHealth({
        filterCriteria: `user_health_employee:eq:${state.hrSelectedUserId}` as any,
        sortCriteria: 'user_health_id:desc' as any,
        limit: 1,
      });
      const row = result.results[0];
      if (row) {
        commit('SET_HR_SELECTED_HEALTH_CHECK_ID', row.id);
        dispatch('setSelectedRecord', row);
        dispatch('rightPanelOpen');
      }
    },
    async hrUpsertOfficialInfo({ commit, state }, data: OfficialInformation) {
      const response = await hrApi.upsertOfficialInfo(data);
      commit('SET_HR_USER', {
        ...state.hrUser,
        officialInformation: response,
      });
      return response;
    },
    async hrUpsertHealthCheck({ commit }, data: HealthCheck) {
      const response = await hrApi.upsertHealthCheck(data);
      commit('SET_HR_HEALTH_CHECK', response);
    },
    async hrUpsertContract({ commit, state }, data: Contract) {
      const response = await hrApi.upsertContract(data);
      commit('SET_HR_CONTRACT', {
        ...state.hrContract,
        contract: response,
      });
      return response;
    },
  },
};

export default module;
